import { useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import AdminAccessDenied from "./AdminAccessDenied";

const NAV = [
  { to: "/admin", label: "Dashboard", end: true },
  { to: "/admin/analytics", label: "Analytics" },
  { to: "/admin/homepage", label: "Homepage" },
  { to: "/admin/media", label: "Media Library" },
  { to: "/admin/releases", label: "Releases & Tracks" },
  { to: "/admin/dimensions", label: "Dimensions" },
  { to: "/admin/visuals", label: "Visuals" },
  { to: "/admin/store", label: "Store" },
  { to: "/admin/vault", label: "Vault" },
  { to: "/admin/inner-circle", label: "Inner Circle" },
  { to: "/admin/network", label: "Motion Network" },
  { to: "/admin/supporters", label: "Supporters" },
  { to: "/admin/whispers", label: "Whispers" },
  { to: "/admin/dimension-v", label: "Dimension V" },
  { to: "/admin/settings", label: "Site Settings" },
];

export default function AdminLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    document.title = "Control Center — Motion & Meaning";
    if (user === false) navigate(`/login?next=${encodeURIComponent(pathname)}`, { replace: true });
  }, [user, navigate, pathname]);

  if (!user) return <div className="min-h-screen bg-[#070707]" />;
  if (user.role !== "admin") return <AdminAccessDenied next={pathname} />;

  return (
    <div className="min-h-screen bg-[#070707] flex" data-testid="admin-layout">
      <aside className="w-52 shrink-0 border-r border-white/10 hidden md:flex flex-col fixed inset-y-0 overflow-y-auto bg-[#050505]">
        <Link to="/" className="block px-5 py-6 border-b border-white/10">
          <p className="font-serif text-base text-white tracking-wide">M<span className="text-gold">&</span>M</p>
          <p className="font-mono text-[8px] tracking-[0.3em] text-white/40 mt-1">CONTROL CENTER</p>
        </Link>
        <nav className="flex-1 py-4">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `block px-5 py-2.5 font-mono text-[10px] tracking-[0.15em] transition-colors ${
                  isActive ? "text-gold bg-white/[0.04] border-r-2 border-gold" : "text-white/50 hover:text-white"}`}>
              {n.label.toUpperCase()}
            </NavLink>
          ))}
        </nav>
        <div className="px-5 py-4 border-t border-white/10">
          <p className="font-mono text-[8px] tracking-[0.2em] text-white/30 truncate">{user.email}</p>
          <Link to="/" className="font-mono text-[9px] tracking-[0.2em] text-white/50 hover:text-gold block mt-2">← VIEW SITE</Link>
        </div>
      </aside>

      <div className="flex-1 md:ml-52">
        <div className="md:hidden border-b border-white/10 px-4 py-3 flex gap-2 overflow-x-auto bg-[#050505] sticky top-0 z-40">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `whitespace-nowrap font-mono text-[9px] tracking-[0.1em] px-3 py-1.5 border ${
                isActive ? "border-gold text-gold" : "border-white/15 text-white/50"}`}>
              {n.label.toUpperCase()}
            </NavLink>
          ))}
        </div>
        <main className="p-6 md:p-10 max-w-6xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
